import { useState } from "react";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db69klkbe24agvuu3ha0";

const subjects = [
  "内容建议",
  "无法播放反馈",
  "版权相关",
  "合作与投稿",
  "其他问题",
];

export default function FeedbackForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      form.reset();
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const v = String(value).trim();
      if (v) payload.append(key, v);
    });

    setStatus("loading");
    setErrorMsg("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && code === "OK") {
        form.reset();
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMsg(String(serverMsg || "提交失败，请稍后重试。"));
      }
    } catch {
      setStatus("error");
      setErrorMsg("网络异常，提交失败，请检查网络后重试。");
    }
  };

  if (status === "success") {
    return (
      <div className="flex flex-col items-center py-10 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
          <i className="ri-check-line text-2xl"></i>
        </span>
        <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
          反馈已提交，感谢你的建议
        </h3>
        <p className="mt-1 text-sm text-foreground-500">
          我们会尽快查看你的反馈，持续优化观看体验。
        </p>
      </div>
    );
  }

  return (
    <form
      id="feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="fb-name" className="mb-1 block text-sm font-medium text-foreground-800">
            称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你"
            className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="mb-1 block text-sm font-medium text-foreground-800">
            邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="用于回复你的反馈"
            className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
      </div>

      <div>
        <label htmlFor="fb-subject" className="mb-1 block text-sm font-medium text-foreground-800">
          反馈类型
        </label>
        <select
          id="fb-subject"
          name="subject"
          required
          defaultValue=""
          className="w-full cursor-pointer rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400"
        >
          <option value="" disabled>
            请选择反馈类型
          </option>
          {subjects.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="fb-message" className="mb-1 block text-sm font-medium text-foreground-800">
          反馈内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="请描述你的建议或遇到的问题（500 字以内）"
          className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
        ></textarea>
      </div>

      <input
        type="text"
        name="website_alt"
        className="addr-helper-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === "error" && (
        <p className="rounded-md bg-accent-100 px-3 py-2 text-sm text-accent-700">{errorMsg}</p>
      )}

      <button
        type="submit"
        disabled={status === "loading"}
        className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {status === "loading" ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            提交中
          </>
        ) : (
          <>
            <i className="ri-send-plane-line"></i>
            提交反馈
          </>
        )}
      </button>
    </form>
  );
}